/* What a coach may do to an assignment once it exists — the rules, not the screen.
 *
 * `docs/WEB-APP-PLAN.md` §12.2 item 3, merging `EVERFIT-MAP.md` §32.4's tier-1 #1 and #2: *the
 * client page cannot DO anything* and *an assignment cannot be changed once made*. They are one
 * screen and one table, so they are one session.
 *
 * PURE on purpose — `assignmentEdit.test.ts` executes it. The Swift harness cannot reach
 * TypeScript, so a `vitest` file is the only place a rule in this app is given teeth.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE WRITES ARE NOT OURS TO INVENT, AND THE PHONE ALREADY PERFORMS ALL FOUR.
 * `CoachAssignmentService.swift` has `reschedule(id:to:)`, `setHidden(id:_:)` and `remove(id:)`,
 * and the note is a plain column. So the RLS policies and the column shapes already exist and
 * this session adds no migration and no security surface. What it adds is the RULES a screen
 * needs in front of them, which the phone does not have either.
 *
 * EXECUTED PER IDENTITY BEFORE ANY OF THIS WAS WRITTEN — `tools/b4-11-assignment-controls/
 * 00-rehearse.sql`, rolled back, 16 arms, three assertions deliberately broken to prove they
 * abort. The four facts it establishes and this file depends on:
 *
 *   | as                | reschedule | note   | hide   | delete |
 *   |-------------------|-----------|--------|--------|--------|
 *   | the COACH         | ACCEPTED  | ACCEPTED | ACCEPTED | ACCEPTED |
 *   | the CLIENT        | P0001     | P0001  | P0001  | rows=0 |
 *   | a STRANGER        | —         | rows=0 | —      | rows=0 |
 *   | anon              | —         | rows=0 | —      | —      |
 *
 * The client's three refusals come from `coach_assignments_guard_client_update`, which permits
 * status / completed_at / completed_snapshot / completed_minutes and nothing else (an ALLOW-LIST since W137's
 * `20261002120000`, which also closed root_workout_id / is_deload / day_position to the client); the CONTROL is that the same
 * client updating `status` is still accepted, so the refusals are the guard rather than an
 * unreadable row.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */

import { isDuplicateDayError, type ExistingAssignment } from "./assign";
import { shortDate } from "./weekLine";

/** The row a control acts on, reduced to what the rules need. */
export type EditableAssignment = {
  id: string;
  scheduledDateISO: string | null;
  /** `coalesce(root_workout_id, workout_id)` — the identity the unique index is keyed on. */
  sourceWorkoutId: string | null;
  hiddenFromClient: boolean;
  note: string | null;
  workoutName: string | null;
};

/* ── MOVING A DAY ───────────────────────────────────────────────────────────────────────────
 *
 * 🔴 THE UNIQUE INDEX ARBITRATES A RESCHEDULE, AND THAT WAS MEASURED RATHER THAN ASSUMED.
 * `coach_assignments_one_source_per_day_idx` is UNIQUE on
 * `(client_id, coalesce(root_workout_id, workout_id), scheduled_date)`. Every word written about
 * it in this repo so far describes an INSERT — `assign.ts`, `AssignPanel.tsx`, V7-2's own record.
 * A reschedule is an UPDATE of the third column, so the same index decides it, and the rehearsal
 * confirms it: moving a row onto a day that already holds the same source workout is refused
 * **23505**, with `coach_assignments_one_source_per_day_idx` named in the message. Its CONTROL is
 * the same statement to a free day, accepted.
 *
 * So the screen predicts that refusal exactly the way `planWrites` predicts it for assigning, and
 * for the same reason: the coach learns before the attempt rather than reading a raw Postgres
 * error afterwards. The database is still the authority — `describeEditOutcome` has an arm for
 * the two disagreeing.
 */
export type RescheduleCheck =
  /** The box is empty, or holds something that is not a `YYYY-MM-DD` day. */
  | { kind: "notADate" }
  /** Already on that day. A write would change nothing and should not be sent. */
  | { kind: "unchanged" }
  /** The unique index will refuse this, and the coach can be told now. */
  | { kind: "collides" }
  | { kind: "free"; dateISO: string };

/**
 * ⚠️ THE ROW BEING MOVED IS EXCLUDED FROM ITS OWN COLLISION TEST — AND A MUTATION CORRECTED THIS
 * COMMENT, WHICH ORIGINALLY CLAIMED THE WRONG REASON.
 *
 * The first version said this filter was what stops an assignment colliding with itself. It is
 * not: `unchanged` returns ABOVE the collision test and catches every self-move the screen can
 * produce, so deleting this clause survived the whole suite. Measured, it is reachable in exactly
 * ONE case — an `existing` list that DISAGREES with the row's own `scheduledDateISO`, i.e. a
 * stale or optimistically updated list. This screen feeds both from ONE fetch, so that is
 * unreachable through the DATA and reachable through the TYPE — the same distinction
 * `training.ts` draws for its own `libraryExerciseId !== null` clause and says out loud.
 *
 * So it is KEPT and given a test that actually reaches it, rather than deleted as dead or left
 * pinned by a case that cannot fail. For the reachable case the two guards are redundant and
 * `unchanged` is the better of them: it answers *"it is already on that day"* where this one
 * would answer *"free"*.
 */
export function checkReschedule(
  current: EditableAssignment,
  toISO: string,
  existing: (ExistingAssignment & { id: string })[],
): RescheduleCheck {
  if (!isDayKey(toISO)) return { kind: "notADate" };
  if (toISO === current.scheduledDateISO) return { kind: "unchanged" };
  const collides = existing.some(
    (a) =>
      a.id !== current.id &&
      a.scheduledDateISO === toISO &&
      a.sourceWorkoutId !== null &&
      a.sourceWorkoutId === current.sourceWorkoutId,
  );
  return collides ? { kind: "collides" } : { kind: "free", dateISO: toISO };
}

/**
 * `YYYY-MM-DD`, and a real day in that month.
 *
 * ⚠️ IT NEVER BUILDS A `Date` FROM THE STRING AND COMPARES, which is the obvious implementation
 * and is wrong here. `new Date("2026-02-30")` is `Invalid Date` in some engines and 2026-03-02 in
 * others; more to the point, `coach_assignments.scheduled_date` is a Postgres `date` with no time
 * and no zone, and `CoachAssignmentService`'s own comment records a whole class of bug from
 * crossing a zone on the way in or out — *"pick Friday, store Thursday, and the coach's week is
 * silently wrong."* An `<input type="date">` hands over exactly this string, so the screen never
 * makes a `Date` at all and the trap is structurally unreachable rather than avoided by care.
 */
export function isDayKey(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  if (m < 1 || m > 12 || d < 1) return false;
  // Day 0 of the NEXT month is the last day of this one, so this is leap-year-correct without
  // restating the rule. `Date.UTC` keeps it off any local zone.
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/* ── SHOWING AND HIDING ─────────────────────────────────────────────────────────────────────
 *
 * `hidden_from_client` is enforced by the CLIENT'S OWN READ POLICY
 * (`client_id = auth.uid() AND hidden_from_client = false`), so a hidden row is genuinely
 * unreadable to them rather than politely skipped — which is also why hiding one takes away
 * their ability to mark it done: `coach_assignments_client_complete` carries the same clause.
 */
export type VisibilityChange = {
  /** What `hidden_from_client` becomes. */
  next: boolean;
  label: string;
  consequence: string;
};

/**
 * ⚠️ HIDING A DAY THEY HAVE ALREADY TRAINED IS A DIFFERENT ACT AND SAYS SO.
 * `client.ts`'s `outcome()` tests `hiddenFromClient` FIRST, so a trained day that is then hidden
 * reads `notShown` rather than `trained`, and `countWindow` moves it out of `assigned` into
 * `notShown`. That is deliberate — *a hidden assignment may never be counted against them* — but
 * it means the counters move under the coach, and a control that does that silently is one they
 * will not trust twice. What they LOGGED is untouched either way: `set_logs` is keyed on the
 * athlete and the day, never on the assignment.
 */
export function visibilityChange(a: EditableAssignment, alreadyTrained: boolean): VisibilityChange {
  if (a.hiddenFromClient) {
    return {
      next: false,
      label: "Show to client",
      consequence: "It appears in their app again and counts as an assigned day.",
    };
  }
  return {
    next: true,
    label: "Hide from client",
    consequence: alreadyTrained
      ? "They have already trained this day. Hiding it takes it out of their app and stops it "
        + "counting as assigned; what they logged stays."
      : "It disappears from their app, and they can no longer mark it done.",
  };
}

/* ── REMOVING ───────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 "REMOVE" MEANS THE ASSIGNMENT, NEVER ITS WORKOUT, AND THE REASON IS A CASCADE.
 * `coach_assignments.workout_id → coach_workouts` is **ON DELETE CASCADE**, so deleting a workout
 * deletes every assignment of it including days already trained — `b4-5`'s ruler 1 exists for
 * exactly that and forbids the builder from reaching the workout row at all.
 *
 * ⚠️ AND IT LEAVES LITTER, MEASURED. V7-2 gives every assignment its own client-scoped COPY of
 * the workout and nothing points from the copy back, so removing the assignment strands the copy:
 * the rehearsal measured orphaned client copies going **0 → 1** on a single delete, against a
 * live baseline of 12 copies / 12 assignments / 0 orphans.
 *
 * The tidier alternative — delete the COPY, which cascades the assignment away in one atomic
 * statement — is REFUSED on failure geometry rather than on taste. Its worst case is a wrong
 * predicate deleting a TEMPLATE and every assignment ever made from it; this one's worst case is
 * a row nothing renders (the library lists `client_id is null` only, and so does the assign
 * panel). A bounded, invisible, reversible cost beats an unbounded one. The cleanup is its own
 * decided act with its own owner, and is written down rather than performed by a side effect.
 */
export type RemovalConsequence = {
  /** True where removing destroys the only record of what was prescribed for a trained day. */
  destroysPrescription: boolean;
  headline: string;
  detail: string;
};

/**
 * ⚠️ THE CONTROL NAMES THE CONSEQUENCE RATHER THAN REFUSING THE ACT.
 * Measured live: **7 of his 12 assignments have logged sets on their own date**, so a blanket
 * refusal would block the majority case, and a bare "are you sure?" over a day carrying 61 logged
 * sets is a confirmation people learn to click through — which is how the real one stops working
 * (`closeAttempt`'s rule, one screen over).
 *
 * What is actually lost is specific and worth saying: the athlete's SETS survive — they live in
 * `set_logs`, keyed on the athlete and the day — but the PRESCRIPTION they were performed against
 * is the client-scoped copy this assignment is the only route to, and it is what the Training
 * tab's whole planned-beside-performed comparison reads. `completed_snapshot` is NULL on all
 * twelve live rows, so there is no second copy of it anywhere.
 */
/** A TASK's removal sentence, beside the workout's so the two shapes cannot drift: a task has no set
 *  logs, only a done mark that lives on the client's own session record — which STAYS. */
export function taskRemovalConsequence(name: string, dayKey: string, doneThatDay: boolean): { headline: string; detail: string } {
  return {
    headline: `Remove ${name} from ${shortDate(dayKey)}?`,
    detail: doneThatDay
      ? "They already marked it done; that record stays with them, but the task you assigned goes. Your library task is not touched."
      : "It has not been done yet, so nothing of theirs is lost. Your library task is not touched.",
  };
}

export function removalConsequence(a: EditableAssignment, loggedSetsThatDay: number): RemovalConsequence {
  const name = a.workoutName ?? "this workout";
  if (loggedSetsThatDay > 0) {
    const n = loggedSetsThatDay;
    return {
      destroysPrescription: true,
      headline: `Remove ${name} from ${a.scheduledDateISO === null ? "this day" : shortDate(a.scheduledDateISO)}?`,
      detail:
        `They logged ${n} set${n === 1 ? "" : "s"} on this day. The ${n === 1 ? "set stays" : "sets stay"}, `
        + "but the workout you assigned goes with it, so this day can no longer be compared "
        + "against what you asked for. Your library workout is not touched.",
    };
  }
  return {
    destroysPrescription: false,
    headline: `Remove ${name} from ${a.scheduledDateISO === null ? "this day" : shortDate(a.scheduledDateISO)}?`,
    detail: "Nothing was logged for this day. Your library workout is not touched.",
  };
}

/* ── THE NOTE ───────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 MEASURED, AND IT CHANGED WHAT THIS SESSION SHIPPED: `coach_assignments.note` HAS TWO WRITERS
 * AND NO READER. The phone's assign sheet writes it, `AssignPanel` writes it, and NOTHING in
 * either app renders it — `AssignedWorkoutBridge` builds the athlete's session description from
 * `workout.summary` and never touches the assignment's note, and the coach's own
 * `ClientDetailView` does not show it either. Live: **0 of 12 assignments carry one**, so nothing
 * has been lost yet.
 *
 * That made the honest choice a fork rather than a feature: shipping an EDITOR for a column
 * nobody reads would be a second writer of a dead field, which is the write-only-member shape
 * this repo deletes. So the note gets a READER on the Training tab in the same change — the coach
 * can now see it, which is the first time anybody could — and `AssignPanel`'s label, which
 * promised *"Note for them"*, is corrected to say who can actually see it. Giving the ATHLETE a
 * reader is phone work and is queued, not silently assumed.
 */
export type NoteWrite =
  | { kind: "unchanged" }
  | { kind: "write"; value: string | null };

/**
 * ⚠️ AN EMPTY BOX MEANS NULL, NOT `""`, AND `unchanged` IS NOT A COURTESY.
 * `AssignPanel` sends `""` on the assign path because `p_note` is non-nullable in the generated
 * RPC signature — a generator artefact it documents rather than argues with. A plain column has
 * no such constraint, so clearing a note here writes NULL, which is what "there is no note"
 * already means on all twelve live rows. And a no-op write is not free: it would move the row,
 * fire the guard trigger, and make the screen refetch to show the coach nothing new.
 */
export function noteWrite(current: string | null, draft: string): NoteWrite {
  const trimmed = draft.trim();
  const next = trimmed === "" ? null : trimmed;
  const now = current === null || current.trim() === "" ? null : current;
  if (next === now) return { kind: "unchanged" };
  return { kind: "write", value: next };
}

/* ── WHAT THE DATABASE SAID ─────────────────────────────────────────────────────────────────*/

export type EditOutcome =
  | { kind: "done" }
  /** The unique index refused a move the prediction thought was free. */
  | { kind: "alreadyOnThatDay" }
  | { kind: "refused"; message: string };

/**
 * ⚠️ ONLY THE DUPLICATE IS TRANSLATED; EVERY OTHER ERROR IS PASSED THROUGH UNTOUCHED.
 * `assign.ts` states the reason and it holds here word for word: a friendly blanket message is
 * the shape `delete-account`'s tolerant `ignorable()` had when it swallowed three column-name
 * typos and returned `{ok: true}` for work it had not done. A message we did not write is the
 * only evidence of a cause we did not anticipate.
 *
 * The duplicate predicate is IMPORTED from `assign.ts` rather than restated, because the index
 * name is the one string both paths must agree on and two copies of it would drift the first time
 * anybody renamed it — which V7-2 already did once.
 */
export function describeEditOutcome(error: { message: string } | null): EditOutcome {
  if (error === null) return { kind: "done" };
  if (isDuplicateDayError(error.message)) return { kind: "alreadyOnThatDay" };
  return { kind: "refused", message: error.message };
}

/**
 * One sentence for the coach, for each thing that can come back.
 *
 * ⚠️ THE SUCCESS SENTENCE IS THE CALLER'S AND THE FAILURE SENTENCE IS BUILT HERE, because they
 * are different grammars: what happened ("Moved to 2026-09-02") against what did not ("Could not
 * move it: …"). One string bent to serve both produces "Moved saved." — which is how a status
 * line stops being read.
 */
export function describeEdit(outcome: EditOutcome, said: { done: string; verb: string }): string {
  switch (outcome.kind) {
    case "done":
      return said.done;
    case "alreadyOnThatDay":
      return "They already have this workout on that day.";
    case "refused":
      return `Could not ${said.verb}: ${outcome.message}`;
  }
}

/** What the reschedule box should say about the day currently typed into it. */
export function describeRescheduleCheck(check: RescheduleCheck): string | null {
  switch (check.kind) {
    case "notADate":
      return "Pick a day to move it to.";
    case "unchanged":
      return "It is already on that day.";
    case "collides":
      return "They already have this workout on that day.";
    case "free":
      return null;
  }
}
